import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  IGetTwoFactorSettingsRes,
  TwoFactorMethodType,
} from "@evennt-tech/api-v2-external";
import { apiV2 } from "api/firebase";
import { registerStore } from "store/utils/reset-store.utils";

type State = {
  settings: IGetTwoFactorSettingsRes | null;
};

type Action = {
  fetchSettings: () => Promise<void>;
  updatePreferredMethod: (method: TwoFactorMethodType) => Promise<void>;
  setMethodConfigured: (
    method: TwoFactorMethodType,
    configured: boolean
  ) => void;
  disable: (password?: string) => Promise<void>;
  confirmPassword: (password: string) => Promise<void>;
  dismissActivationPrompt: () => Promise<void>;
};

export const useTwoFactorSettings = create(
  devtools<State & Action>(
    (set, get) => ({
      settings: null,

      fetchSettings: async () => {
        try {
          const settings = await apiV2.twoFactorSettings.get();
          set({ settings });
        } catch (error) {
          console.error("Failed to fetch two-factor settings:", error);
        }
      },

      updatePreferredMethod: async (method) => {
        await apiV2.twoFactorSettings.updatePreferredMethod({ method });
        await get().fetchSettings();
      },

      setMethodConfigured: (method, configured) =>
        set((state) => {
          if (!state.settings) return state;

          const methods = state.settings.methods.map((item) =>
            item.method === method ? { ...item, configured } : item
          );
          const enabled = methods.some((item) => item.configured);
          const preferredMethod = !enabled
            ? null
            : state.settings.preferredMethod === method && !configured
            ? null
            : state.settings.preferredMethod;

          return {
            settings: { ...state.settings, methods, enabled, preferredMethod },
          };
        }),

      disable: async (password) => {
        await apiV2.twoFactorSettings.disable({ password });
        await get().fetchSettings();
      },

      confirmPassword: async (password) => {
        await apiV2.twoFactorSettings.confirmPassword({ password });
      },

      dismissActivationPrompt: async () => {
        await apiV2.twoFactorSettings.dismissActivationPrompt();
        set((state) =>
          state.settings
            ? {
                settings: {
                  ...state.settings,
                  activationPromptDismissed: true,
                },
              }
            : state
        );
      },
    }),
    { name: "two-factor-settings" }
  )
);

registerStore(useTwoFactorSettings);
